import React, { useContext } from 'react';
import { AlertMessage } from '@eposnow/ui-core';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UIContext } from '../context/UIContext';
import { UserContext } from '../context/UserContext';
import EnhancedSnackbar from './EnhancedSnackbar';
import { Banner } from '../api/promotionalBanner';

interface WarningBannerProps {
  banner: Banner;
  isLoading?: boolean;
  isSubmitting?: boolean;
  isError?: boolean;
  submitError?: boolean;
  onDismiss: () => void;
}

const WarningBanner = ({
  banner,
  isLoading,
  isSubmitting,
  isError,
  submitError,
  onDismiss,
}: WarningBannerProps) => {
  const theme = useTheme();
  const { isMobile, isTablet } = useContext(UIContext);
  const { locale } = useContext(UserContext);
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <Box
        sx={{
          py: 2,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  const title = t(`components.maintenanceBanner.${banner.feature}.title`);

  const description = t(`components.maintenanceBanner.${banner.feature}.description`);

  const buttonText = t(`components.maintenanceBanner.${banner.feature}.buttonText`);

  return (
    <>
      <AlertMessage
        type="warning"
        locale={locale}
        theme={theme}
        styles={{ marginBottom: 2 }}
        iconAlignment="baseline"
        isMobile={isMobile || isTablet}
      >
        <Box
          data-qa-id={`maintenance-${banner.feature}`}
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography sx={{ fontWeight: 'bold' }}>
            {title}
          </Typography>

          <Typography>{description}</Typography>

          <Button
            data-qa-id="gotItWarningButton"
            disabled={isSubmitting}
            sx={{
              marginLeft: 'auto',
              fontWeight: 'bold',
              color: theme.palette.warning.dark,
            }}
            onClick={onDismiss}
          >
            {buttonText}
          </Button>
        </Box>
      </AlertMessage>

      <EnhancedSnackbar
        open={!!(isError || submitError)}
        message={t('components.error.somethingFailed')}
        severity="error"
      />
    </>
  );
};

export default WarningBanner;
